/**
 * TeamAccessBoardConnected — the "Team Access" board wired to the live surface,
 * for the Home detail pane and the apps/team-access satellite. Wrapper-as-adapter
 * (allocation precedent): fetches the frozen wire endpoints, MAPS them into the
 * presentational TeamAccessBoard's view-model, and executes decide/grant/revoke.
 *
 * Data (unit scope via useUnitScope — profile unit → managed-units catalogue,
 * so an admin or a multi-unit lead is never dead-ended on a missing profile
 * unit):
 *   GET /me/access/overview                 → managed_locations (scope catalogue)
 *   GET /locations/{id}/roster        → roster + per-member access
 *   GET /access-requests?decidable_by=me    → the approver queue
 *
 * Writes are SERVER-AUTHORITATIVE (clinical-safety carve-out): every decide /
 * grant / revoke calls the REST endpoint, then invalidates the access queries —
 * access is NEVER optimistically flipped. Each carries an Idempotency-Key.
 * The unit pick is LOCAL from the session cut-over on: the session→location
 * link was a session_assign row, and a session cites a subject now — nothing
 * frames it to a unit (AllocationBoardConnected precedent).
 *
 * Chat side-channel: the agent's `access_commands` blocks fold into
 * AccessProposalCards the lead confirms with one tap, executing via the SAME
 * REST endpoints as the board's own controls (allocation ghost-proposal
 * precedent). The composer works session-less too — its first send mints the
 * session via useDeferredFirstSend, then `onSessionCreated` lets the caller
 * transition into the real conversation; the proposal-card FOLD itself
 * (reading session.messages) stays session-gated, same as BoardAgentChat's
 * transcript — nothing to fold before a session exists.
 *
 * Shift config: the frontend GET /config has no single "end of this shift"
 * boundary (admin.shifts is server-only; a roster is a multi-shift
 * catalogue with no current-shift resolver), so the composer's end-of-shift
 * quick-pick is disabled and relative quick-picks are offered instead — the
 * connected layer never fabricates a clock value.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuthStore } from '@/stores/auth';
import { useStreamingMessage } from '@/hooks/useStreamingChat';
import { useDeferredFirstSend } from '@/hooks/useDeferredFirstSend';
import { sessionKeys, type SessionDetail } from '@/hooks/useSessions';
import { BoardAgentChat, ChatInput } from '@/components/chat';
import { Skeleton } from '@/components/ui/skeleton';
import { mainAppUrl } from '@/api/mainAppUrl';
import { AGENT_SLUG } from '../../agent';
import {
  accessKeys,
  isUnitAuthorityDenied,
  useAccessRequestInbox,
  useUnitAccess,
  useUnitScope,
} from '@/hooks/useAccess';
import {
  revokeLocationShare,
  shareLocationResource,
  type UnitAccessMember,
  type AccessRef,
} from '@/api/endpoints/access';
import { decideAccessRequest, type AccessRequest as AccessRequestWire } from '@/api/endpoints/accessRequests';
import type { SessionResponse } from '@/api/types_session';

import { TeamAccessBoard } from './TeamAccessBoard';
import { AccessProposalCard } from './AccessProposalCard';
import { NoUnitAuthority, UnitChooser } from '@frontend/components/access/UnitScopePanels';
import { foldAccess, type AccessCommand } from './foldAccess';
import type {
  AccessGrant,
  AccessProposal,
  AccessProposalAction,
  AccessRequest,
  GrantDraft,
  GrantExpiry,
  TeamMember,
} from '@frontend/components/access/types';
import type { GrantSubjectOption } from './TeamAccessBoard';

interface TeamAccessBoardConnectedProps {
  /** Present on the Session page / detail pane; absent on the session-less
   *  Home face (board-only + a session-less composer, see useDeferredFirstSend). */
  session?: SessionDetail;
  /** Face mode only. Team Access has no patient frame (team scope), so this is
   *  always undefined in practice — present for AgentDetailViewProps parity. */
  facePatientId?: string | null;
  /** Face mode only — fires once the composer's first send mints the session. */
  onSessionCreated?: (session: SessionResponse) => void;
}

const grantRowKey = (userId: string, grant: AccessGrant) =>
  `${userId}:${grant.resource.type}:${grant.resource.id}`;

const refToResource = (ref: AccessRef) => ({
  type: ref.type,
  id: ref.id,
  label: ref.label && ref.label.trim() !== '' ? ref.label : ref.id,
});

function mapRequest(r: AccessRequestWire): AccessRequest {
  return {
    id: r.id,
    requester: { id: r.requested_by, name: r.requester_name ?? r.requested_by },
    resource: {
      type: r.resource_type,
      id: r.resource_id,
      label: r.resource_title && r.resource_title.trim() !== '' ? r.resource_title : r.resource_id,
    },
    relation: r.access_level,
    justification: r.justification,
    status: r.status,
    created_at: r.created_at,
    decided_at: r.decided_at,
    decided_by: r.decided_by ? { id: r.decided_by, name: '' } : undefined,
  };
}

/** Resolve the composer's expiry sentinel to an ISO cutoff (or none). */
function resolveExpiry(e: GrantExpiry): string | undefined {
  if (e.kind === 'until') return e.at;
  // 'permanent' → no expiry. 'end_of_shift' is disabled (no shift boundary on
  // GET /config), so it never reaches here; guarded rather than silently
  // treated as permanent.
  return undefined;
}

export function TeamAccessBoardConnected({ session, onSessionCreated }: TeamAccessBoardConnectedProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const me = useAuthStore((s) => s.user);

  // Framing is the session OWNER's act (PUT /sessions/{id}/location is
  // sessionOwner-gated): only the author gets the chooser/switcher — a shared
  // viewer would only ever collect a 403 (AllocationBoardConnected precedent).
  // Fail closed on a missing author_id; a session-less board is the caller's
  // own surface, so they always "author" the local pick.
  const isAuthor = session ? session.session.created_by.performer === me?.id : true;

  // Unit scope: a LOCAL pick, then profile unit → managed-units catalogue
  // (useUnitScope). The session→location link that used to persist the pick is
  // gone with the session cut-over (AllocationBoardConnected precedent).
  const [localUnitId, setLocalUnitId] = useState<string | undefined>();
  const scope = useUnitScope(localUnitId ?? undefined);
  const locationId = scope.status === 'ready' ? scope.unit.id : undefined;
  const board = useUnitAccess(locationId);
  const inbox = useAccessRequestInbox();

  const pickUnit = (locId: string) => {
    setLocalUnitId(locId);
  };

  const invalidateAccess = () => {
    void queryClient.invalidateQueries({ queryKey: accessKeys.unit(locationId ?? '') });
    void queryClient.invalidateQueries({ queryKey: accessKeys.inbox() });
    if (session?.session.id) {
      void queryClient.invalidateQueries({ queryKey: sessionKeys.detail(session.session.id) });
    }
  };

  // --- Mutation in-flight tracking (per-item, for the board's pending props) ---
  const [decidingIds, setDecidingIds] = useState<string[]>([]);
  const [revokingKeys, setRevokingKeys] = useState<string[]>([]);
  const [confirmingIds, setConfirmingIds] = useState<string[]>([]);

  const decideMut = useMutation({
    mutationFn: ({ requestId, decision }: { requestId: string; decision: 'approve' | 'deny' }) =>
      decideAccessRequest(requestId, decision),
    onMutate: ({ requestId }) => setDecidingIds((ids) => [...ids, requestId]),
    onSettled: (_d, _e, { requestId }) => {
      setDecidingIds((ids) => ids.filter((id) => id !== requestId));
      invalidateAccess();
    },
  });

  const grantMut = useMutation({
    mutationFn: (draft: GrantDraft) =>
      shareLocationResource(locationId as string, {
        principal: { type: draft.subject.type, id: draft.subject.id },
        access_level: draft.role as 'owner' | 'editor' | 'viewer',
        expires_at: resolveExpiry(draft.expiry),
      }),
    onSettled: () => invalidateAccess(),
  });

  const revokeMut = useMutation({
    mutationFn: (vars: { member: TeamMember; grant: AccessGrant }) =>
      revokeLocationShare(locationId as string, 'user', vars.member.user.id),
    onMutate: ({ member, grant }) =>
      setRevokingKeys((ks) => [...ks, grantRowKey(member.user.id, grant)]),
    onSettled: (_d, _e, { member, grant }) => {
      setRevokingKeys((ks) => ks.filter((k) => k !== grantRowKey(member.user.id, grant)));
      invalidateAccess();
    },
  });

  // --- View-model assembly ---
  const teamVM = useMemo(() => {
    if (!board.data || scope.status !== 'ready') return null;
    const unit = refToResource(scope.unit);

    const members: TeamMember[] = board.data.members.map((m: UnitAccessMember) => {
      const grants: AccessGrant[] = m.node_grants.map((g) => ({
        resource: unit,
        role: g.relation,
        source: 'direct' as const,
        expires_at: g.expires_at,
      }));
      return {
        user: { id: m.user_id, name: m.name ?? m.user_id },
        roles: m.roles.map((r) => r.name || r.group_id),
        grants,
        expiring: grants.filter((g) => !!g.expires_at),
        open_requests: m.open_requests.map(mapRequest),
      };
    });

    // Grantable subjects: roster members + the deduped role groups they hold.
    const subjects: GrantSubjectOption[] = [];
    const seen = new Set<string>();
    for (const m of board.data.members) {
      const key = `user:${m.user_id}`;
      if (!seen.has(key)) {
        seen.add(key);
        subjects.push({ type: 'user', id: m.user_id, label: m.name ?? m.user_id });
      }
      for (const role of m.roles) {
        const gkey = `group:${role.group_id}`;
        if (!seen.has(gkey)) {
          seen.add(gkey);
          subjects.push({ type: 'group', id: role.group_id, label: role.name || role.group_id });
        }
      }
    }

    return {
      team: { label: unit.label },
      members,
      subjects,
      resources: [unit],
      unit,
    };
  }, [board.data, scope]);

  // --- Chat side-channel: folded access_commands → confirmable proposals ---
  // `members`, not `members ?? []`: the fallback allocates a new array on every
  // render while the board is loading, so a memo keyed on it would re-fold the
  // whole command log each time. Default inside the memo instead.
  const members = board.data?.members;
  const proposals: AccessProposal[] = useMemo(() => {
    const roster = members ?? [];
    const commands = foldAccess(session?.messages.items);
    const byId = new Map(roster.map((m) => [m.user_id, m.name ?? m.user_id]));
    const pendingIds = new Set((inbox.data?.requests ?? []).map((r) => r.id));
    return commands.flatMap((c) => {
      // A 'deny' has no proposal-card representation (AccessProposalAction is
      // grant|revoke|approve); denials are made through the inbox's Deny button.
      if (c.action === 'deny') return [];
      // An approve card for a request that is no longer pending (decided —
      // usually by confirming THIS card) has nothing left to act on: drop it
      // instead of leaving a stale card that, after a unit switch, would even
      // mislabel itself with the current unit (approve commands carry no
      // resource). Only filtered once the inbox has loaded, so cards don't
      // flash out during a refetch.
      if (c.action === 'approve' && inbox.data && (!c.request_id || !pendingIds.has(c.request_id))) {
        return [];
      }
      // A group-principal grant/revoke can't round-trip through the user-centric
      // AccessProposal (subject is a person). The composer grants to role groups
      // directly; skip here rather than mis-target a person with a group id.
      if ((c.action === 'grant' || c.action === 'revoke') && c.principal && c.principal.type !== 'user') {
        return [];
      }
      const action: AccessProposalAction = c.action;
      return [mapCommand(action, c, byId, teamVM?.unit)];
    });
  }, [session?.messages, members, teamVM?.unit, inbox.data]);

  const confirmProposal = (p: AccessProposal) => {
    setConfirmingIds((ids) => [...ids, p.id]);
    const done = () => {
      setConfirmingIds((ids) => ids.filter((id) => id !== p.id));
      invalidateAccess();
    };
    if (p.action === 'approve') {
      if (!p.request_id) return done();
      decideAccessRequest(p.request_id, 'approve').finally(done);
      return;
    }
    if (p.action === 'grant') {
      shareLocationResource(locationId as string, {
        principal: { type: 'user', id: p.subject.id },
        access_level: (p.role || 'viewer') as 'owner' | 'editor' | 'viewer',
        expires_at: p.expires_at,
      }).finally(done);
      return;
    }
    // revoke
    revokeLocationShare(locationId as string, 'user', p.subject.id).finally(done);
  };
  const [dismissedProposalIds, setDismissedProposalIds] = useState<string[]>([]);
  const visibleProposals = proposals.filter((p) => !dismissedProposalIds.includes(p.id));

  const streaming = useStreamingMessage(session?.session.id ?? '');
  // Team Access has no patient frame — nothing to pass as patientId here
  // (unlike CarePlanBoardConnected/AllocationBoardConnected, which are patient-scoped).
  const deferred = useDeferredFirstSend({
    sessionId: session?.session.id,
    agentSlug: AGENT_SLUG,
    sendMessage: streaming.sendMessage,
    onCreated: (newSession) => {
      onSessionCreated?.(newSession);
    },
  });
  const wasStreaming = useRef(false);
  useEffect(() => {
    if (streaming.isStreaming) {
      wasStreaming.current = true;
    } else if (wasStreaming.current) {
      wasStreaming.current = false;
      // A finished turn may have emitted proposals AND the lead may have acted
      // between turns — refresh the board, inbox and the session's messages.
      void queryClient.invalidateQueries({ queryKey: accessKeys.unit(locationId ?? '') });
      void queryClient.invalidateQueries({ queryKey: accessKeys.inbox() });
      if (session?.session.id) {
        void queryClient.invalidateQueries({ queryKey: sessionKeys.detail(session.session.id) });
      }
    }
  }, [streaming.isStreaming, locationId, session?.session.id, queryClient]);

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="min-h-0 flex-1 overflow-auto">
        {scope.status === 'loading' ? (
          <BoardSkeleton />
        ) : scope.status === 'error' ? (
          <LoadRetry
            message={t('access.team.loadError', {
              defaultValue: 'Could not load the team access board.',
            })}
            onRetry={scope.retry}
          />
        ) : scope.status === 'none' || scope.status === 'choose' ? (
          !isAuthor ? (
            // A shared viewer of an ungrounded session: only the author can
            // frame it, so offer no picker whose PUT could only 403.
            <div className="grid h-full place-items-center p-8 text-center">
              <p className="text-[15px] text-neutral-500">
                {t('access.team.notFramed', {
                  defaultValue: 'This conversation isn’t framed to a unit yet.',
                })}
              </p>
            </div>
          ) : scope.status === 'none' ? (
            <NoUnitAuthority onOpenProfile={() => window.location.assign(mainAppUrl('/profile'))} />
          ) : (
            <UnitChooser
              options={scope.options}
              onPick={pickUnit}
            />
          )
        ) : teamVM ? (
          <>
            {visibleProposals.length > 0 && (
              <div className="mx-auto w-full max-w-4xl space-y-2 px-4 pt-4">
                {visibleProposals.map((p) => (
                  <AccessProposalCard
                    key={p.id}
                    proposal={p}
                    pending={confirmingIds.includes(p.id)}
                    onConfirm={confirmProposal}
                    onDismiss={(pr) => setDismissedProposalIds((ids) => [...ids, pr.id])}
                  />
                ))}
              </div>
            )}
            <TeamAccessBoard
              team={teamVM.team}
              members={teamVM.members}
              requests={(inbox.data?.requests ?? []).map(mapRequest)}
              grantableSubjects={teamVM.subjects}
              grantableResources={teamVM.resources}
              grantableRoles={['viewer', 'editor']}
              shiftEndAvailable={false}
              unitOptions={scope.options.map(refToResource)}
              activeUnitId={locationId}
              onSwitchUnit={isAuthor && scope.options.length > 1 ? pickUnit : undefined}
              onDecide={(requestId, decision) => decideMut.mutate({ requestId, decision })}
              decidingIds={decidingIds}
              onGrant={(draft) => grantMut.mutate(draft)}
              granting={grantMut.isPending}
              onRevoke={(member, grant) => revokeMut.mutate({ member, grant })}
              revokingKeys={revokingKeys}
            />
          </>
        ) : board.isError ? (
          isUnitAuthorityDenied(board.error) ? (
            <NoUnitAuthority
              onOpenProfile={() => window.location.assign(mainAppUrl('/profile'))}
              deniedMessage={board.error.message}
            />
          ) : (
            <LoadRetry
              message={t('access.team.loadError', {
                defaultValue: 'Could not load the team access board.',
              })}
              onRetry={() => void board.refetch()}
            />
          )
        ) : (
          <BoardSkeleton />
        )}
      </div>

      {session && (
        <BoardAgentChat
          session={session}
          streaming={streaming}
          className="max-h-[35%] shrink-0 overflow-y-auto border-t border-neutral-200 bg-neutral-50 px-4 py-2"
        />
      )}

      {/* The composer only appears once the board is grounded in a unit — an
          ungrounded agent would answer without roster/inbox context, so the
          chooser (above) is the one call to action until then. Session or a
          way to create one (face mode passes onSessionCreated). */}
      {locationId && (session || onSessionCreated) && (
        <div className="shrink-0 border-t border-neutral-200 bg-neutral-50 px-4 py-3 pb-safe">
          <ChatInput
            onSend={(content) => void deferred.send(content)}
            isPending={streaming.isStreaming || deferred.isPending}
            placeholder={t('access.team.chatPlaceholder', {
              defaultValue: 'Ask the agent to propose a grant…',
            })}
          />
        </div>
      )}
    </div>
  );
}

function BoardSkeleton() {
  return (
    <div className="mx-auto w-full max-w-4xl space-y-3 p-4" aria-busy="true">
      <Skeleton className="h-24 w-full rounded-[18px]" />
      <Skeleton className="h-20 w-full rounded-[18px]" />
      <Skeleton className="h-20 w-full rounded-[18px]" />
    </div>
  );
}

function LoadRetry({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="p-4">
      <p className="text-[15px] text-neutral-700" role="alert">
        {message}{' '}
        <button
          type="button"
          onClick={onRetry}
          className="font-semibold text-primary underline-offset-2 hover:underline"
        >
          {t('actions.retry', 'Retry')}
        </button>
      </p>
    </div>
  );
}

/** Map a folded AccessCommand → the AccessProposalCard's AccessProposal. */
function mapCommand(
  action: AccessProposalAction,
  c: AccessCommand,
  rosterNames: Map<string, string>,
  unit: { type: string; id: string; label: string } | undefined,
): AccessProposal {
  const subjectId = c.principal?.id ?? '';
  const resource = c.resource
    ? { type: c.resource.type, id: c.resource.id, label: c.resource.label ?? c.resource.id }
    : (unit ?? { type: 'node', id: '', label: '' });
  return {
    id: `${action}:${subjectId}:${resource.type}:${resource.id}:${c.request_id ?? ''}`,
    action,
    subject: { id: subjectId, name: rosterNames.get(subjectId) ?? subjectId },
    resource,
    role: c.role ?? 'viewer',
    expires_at: c.expires_at,
    request_id: c.request_id,
  };
}
